import { createPreloadTrials, generateAssetObject } from '@bdelab/roar-utils';
import store from 'store2';
import { jsPsych } from './jsPsych';
import Assets from '../../assets.json';

const bucketURI = 'https://storage.googleapis.com/roar-vocab';

export const mediaAssets = generateAssetObject(Assets, bucketURI);

export const preloadTrials = createPreloadTrials(Assets, bucketURI).default;

export const waitFor = (conditionFunction) => {
  const poll = (resolve) => {
    if (conditionFunction()) resolve();
    // eslint-disable-next-line no-unused-vars
    else setTimeout((_) => poll(resolve), 400);
  };

  return new Promise(poll);
};

/* simple variable for calculating sum of an array */
const arrSum = (arr) => arr.reduce((a, b) => a + b, 0);

export const shuffle = (array) => {
  const shuffledArray = [...array];
  for (let i = shuffledArray.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1)); // random index from 0 to i
    // swap elements array[i] and array[j]
    // use "destructuring assignment" syntax
    // eslint-disable-next-line no-param-reassign
    [shuffledArray[i], shuffledArray[j]] = [shuffledArray[j], shuffledArray[i]];
  }
  return shuffledArray;
};

export const updateProgressBar = (ifComplete) => {
  if (!ifComplete) {
    const curr_progress_bar_value = jsPsych.getProgressBarCompleted();
    jsPsych.setProgressBar(curr_progress_bar_value + 1 / arrSum(store.session.get('config').stimulusCountList));
  } else {
    jsPsych.setProgressBar(1);
  }
};

const prepareNextStimulus = (resultStimulus, numAFC, itemType) => {
  // update which picture shows the target picture:
  const randIndex = Math.floor(Math.random() * numAFC);
  store.session.set('targetRespNum', randIndex);

  const stimulus = shuffle(resultStimulus);
  const choices = [];
  const words = [];
  for (let i = 0; i < numAFC - 1; i += 1) {
    choices.push(stimulus[i].img2);
    words.push(stimulus[i].word2);
  }
  choices.splice(store.session('targetRespNum'), 0, stimulus[0].img1);
  words.splice(store.session('targetRespNum'), 0, stimulus[0].word1);
  return {
    target: stimulus[0].word1,
    choices: choices,
    words: words,
    itemType: itemType,
    itemSource: stimulus[0].source,
  };
};

export const getPracticeStimulusMultiAFC = (numAFC) => {
  let resultStimulus = [];
  const currentCorpus = store.session('corpusPractice');
  resultStimulus = [currentCorpus[store.session('corpusPracticeIndex')].slice(0, numAFC - 1)];

  store.session.transact('corpusPracticeIndex', (oldVal) => oldVal + 1);

  // update which picture shows the target picture:
  return prepareNextStimulus(resultStimulus[0], numAFC, 'practice');
};

export const getStimulusMultiAFC = (numAFC) => {
  let resultStimulus = [];
  // update 2 trackers
  const currentBlockIndex = store.session('currentBlockIndex');
  const tracker = store.session('trialNumBlock');

  if (tracker === 0 || tracker === store.session('config').stimulusCountList[currentBlockIndex]) {
    store.session.set('trialNumBlock', 1);
  } else {
    store.session.transact('trialNumBlock', (oldVal) => oldVal + 1);
  }
  store.session.transact('trialNumTotal', (oldVal) => oldVal + 1);
  const { randomCatchIndex } = store.session.get('config');
  if (randomCatchIndex[currentBlockIndex] === store.session.get('trialNumBlock')) {
    // check if this should be a catch trial
    resultStimulus = store.session.get('corpusCatch')[currentBlockIndex].slice(0, numAFC - 1);
    return prepareNextStimulus(resultStimulus, numAFC, 'catch');
  }

  // else: call the test corpus
  const currentCorpus = store.session('corpusTest');
  resultStimulus = currentCorpus[store.session.get('corpusTestIndex')].slice(0, numAFC - 1);
  store.session.transact('corpusTestIndex', (oldVal) => oldVal + 1);
  return prepareNextStimulus(resultStimulus, numAFC, 'test');
};

export const processSurveyData = (data) => {
  const tmpMetadata = {};
  for (const field in data.response) {
    if (data.response[field] === '') {
      tmpMetadata[field] = null;
    } else if (field === 'ell') {
      if (data.response[field] === 'YES') {
        tmpMetadata[field] = 0;
      } else {
        tmpMetadata[field] = 1;
      }
    } else if (field === 'retake') {
      if (data.response[field] === 'YES') {
        tmpMetadata[field] = 1;
      } else {
        tmpMetadata[field] = 0;
      }
    } else {
      tmpMetadata[field] = data.response[field];
    }
  }
  // TO DO: fix the survey part
  const config = store.session.get('config');
  config.userMetadata = { ...config.userMetadata, ...tmpMetadata };
  store.session.set('config', config);

  function init_audio_files() {
    jsPsych.pluginAPI.audioContext();
  }
  document.addEventListener('touchstart', init_audio_files, { once: true });
  document.addEventListener('click', init_audio_files, { once: true });
};

// This feature allows the caller to set a time limit for the app, configured via url and store variable maxTime
// the preload time is not included in the time limit
// initAppTimer() can be called in initTimeline to limit the duration of the entire app
// Be sure to call clearTimeout() before exiting

export const initAppTimer = () => {
  if (store.session('config').maxTime) {
    // create a timer to flag that it's time to exit the app
    const maxTimeMs = store.session('config').maxTime * 60000;

    const timerId = setTimeout(() => {
      store.session.set('maxTimeReached', true);
    }, maxTimeMs);

    store.session.set('maxTimerId', timerId);
  }

  // init to false, when there is no timeout configured it will never be true
  store.session.set('maxTimeReached', false);
};

export const isMaxTimeoutReached = () => store.session('maxTimeReached');
